import { ComputedOrImmediate } from '@funkey/shared';
import { Data, Unify } from 'effect';
import { useMemo, useState } from 'react';

export declare namespace useTaggedEnumState {
    export type State<$Enum extends Tagged, $State extends $Enum = $Enum> = {
        state: $State;
        setState: React.Dispatch<React.SetStateAction<$Enum>>;

        enum: Data.TaggedEnum.Constructor<$Enum>;

        $is<$Tag extends $Enum['_tag']>(
            tag: $Tag,
        ): this is State<$Enum, Extract<$Enum, Tagged<$Tag>>>;

        $match<
            const Cases extends {
                readonly [$Tag in $Enum['_tag']]: (args: Extract<$Enum, Tagged<$Tag>>) => any;
            },
        >(
            cases: Cases & { [K in Exclude<keyof Cases, $Enum['_tag']>]: never },
        ): Unify.Unify<ReturnType<Cases[$Enum['_tag']]>>;
    } & {
        [$Tag in $Enum['_tag'] as `set${$Tag}`]: (
            newState: ComputedOrImmediate<(prev: $State) => Omit<Tagged.get<$Enum, $Tag>, '_tag'>>,
        ) => void;
    } & {
        [$Tag in $Enum['_tag'] as `is${$Tag}`]: () => this is State<
            $Enum,
            Extract<$Enum, Tagged<$Tag>>
        >;
    };
    type Tagged<T extends string = string> = { readonly _tag: T };
    namespace Tagged {
        export type get<$Tagged, $Tag extends string> = Extract<$Tagged, Tagged<$Tag>>;
    }
}
export function useTaggedEnumState<T extends { readonly _tag: string }>(
    initialState: ComputedOrImmediate<(taggedEnum: Data.TaggedEnum.Constructor<T>) => NoInfer<T>>,
): useTaggedEnumState.State<T> {
    const taggedEnum = useMemo(() => Data.taggedEnum<T>(), []);

    const [state, setState] = useState(() => ComputedOrImmediate.get(initialState, taggedEnum));

    // prettier-ignore
    return useMemo<any>(() => new Proxy({
        state,
        setState,
        $is(tag: string) {
            // @ts-ignore
            return taggedEnum.$is(tag)(state)
        },
        $match(cases: any) {
            // @ts-ignore
            return taggedEnum.$match(cases)(state)
        }
    }, {
        get(target, p, _receiver) {
            switch (p) {
                case 'state':
                case 'setState':
                case '$is':
                case '$match':
                    return target[p]

                case 'enum':
                    return taggedEnum;
                default:
                    if (typeof p !== 'string') return;

                    if (p.startsWith('set')) {
                        const tag = p.slice(3)
                        return (value: ComputedOrImmediate<(prev: any) => any>) => target.setState(
                            prev => (taggedEnum[tag as keyof typeof taggedEnum] as any)(ComputedOrImmediate.get(value, prev))
                        );
                    }
                    if (p.startsWith('is')) {
                        const tag = p.slice(2)
                        return () => target.$is(tag);
                    }

            }
        }
    }), [state, setState, taggedEnum]);
}
